/* eslint-env browser */
// Contact us (owner, 5 Nov 2026: "i like what you did to about us page, can you do the
// same for contact us page"), in About us's look (components/landing/SitePage): the night
// hero; the ways to reach the company on cards over the hero's edge (call, email, visit:
// from /settings, only those that are set; the address opens in Google Maps); the social
// links that are set, as tiles drawn in the theme's ink, not the brands' colours; and the
// app band. Words: ct_* keys with English defaults, am / om / ti in
// json/languages-landing.add.json and bundled in components/landing/landingWords.js.
// Customer and trip, never rider or ride.
import React from 'react';
import { useSelector } from 'react-redux';
import { Box, Container, Typography } from '@mui/material';
import SvgIcon from '@mui/material/SvgIcon';
import {
  ArrowForward,
  PhoneOutlined,
  MailOutlineRounded,
  PlaceOutlined,
  Telegram,
  YouTube,
  Instagram,
  Facebook,
} from '@mui/icons-material';
import Reveal from '../components/landing/Reveal';
import { srOnly } from '../components/landing/StoreButtons';
import SitePage, { NightHero, AppBand, useSiteWords, eyebrowSx, h2Sx } from '../components/landing/SitePage';
import TikTokIcon from '../components/TikTokIcon';
import { tokens as c, iconHues } from '../theme';

// X's mark, sized like the Material icons beside it (about 19 px wide in a 24-px box).
const XIcon = (props) => (
  <SvgIcon viewBox="-34 -35 581 581" {...props}>
    <path d="M389.2 48h70.6L305.6 224.2 487 464H345L233.7 318.6 106.5 464H35.8L200.7 275.5 26.8 48H172.4L272.9 180.9 389.2 48zM364.4 421.8h39.1L151.1 88h-42L364.4 421.8z" />
  </SvgIcon>
);

// A card or tile that is one link: a lift and an orange edge on hover, a ring on focus.
const cardLinkSx = {
  textDecoration: 'none',
  color: c.INK,
  bgcolor: c.WHITE,
  border: `1px solid ${c.OUTLINE}`,
  boxSizing: 'border-box',
  height: '100%',
  transition: 'transform 160ms ease-out, border-color 160ms ease-out, color 160ms ease-out',
  '&:hover': { transform: 'translateY(-3px)', borderColor: c.ACCENT },
  '&:focus-visible': { outline: `3px solid ${c.ACCENT_TEXT}`, outlineOffset: 3 },
  '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } },
};

export default function ContactUs(props) {
  const { t, L, track } = useSiteWords();
  const settings = useSelector((state) => state.settingsdata.settings) || {};
  const newTab = (
    <Box component="span" sx={srOnly}>
      {' ' + L('lp_new_tab', '(opens in a new tab)')}
    </Box>
  );

  // The ways to reach the company, as /settings has them (as before); only those that are set.
  const phone = settings.CompanyPhone;
  const ways = [
    phone
      ? { id: 'call', icon: <PhoneOutlined />, hue: iconHues.success, title: L('ct_call_t', 'Call us'), value: phone, href: 'tel:' + String(phone).replace(/[^\d+]/g, ''), action: L('ct_call_action', 'Call now') }
      : null,
    settings.contact_email
      ? { id: 'email', icon: <MailOutlineRounded />, hue: iconHues.info, title: L('ct_email_t', 'Email us'), value: settings.contact_email, href: 'mailto:' + settings.contact_email, action: L('ct_email_action', 'Write to us') }
      : null,
    settings.CompanyAddress
      ? {
          id: 'visit',
          icon: <PlaceOutlined />,
          hue: iconHues.violet,
          title: L('ct_visit_t', 'Visit us'),
          value: settings.CompanyAddress,
          note: settings.CompanyName,
          href: 'https://www.google.com/maps/search/?api=1&query=' + encodeURIComponent(settings.CompanyAddress),
          external: true,
          action: L('ct_visit_action', 'Open in Maps'),
        }
      : null,
  ].filter(Boolean);

  // The social links that are set, in the old order; brand names stay as they are.
  const socials = [
    { key: 'telegram', name: 'Telegram', href: settings.TelegramHandle, icon: <Telegram /> },
    { key: 'tiktok', name: 'TikTok', href: settings.TikTokHandle, icon: <TikTokIcon /> },
    { key: 'youtube', name: 'YouTube', href: settings.YouTubeHandle, icon: <YouTube /> },
    { key: 'instagram', name: 'Instagram', href: settings.InstagramHandle, icon: <Instagram /> },
    { key: 'facebook', name: 'Facebook', href: settings.FacebookHandle, icon: <Facebook /> },
    { key: 'x', name: 'X', href: settings.TwitterHandle, icon: <XIcon /> },
  ].filter((s) => s.href);

  return (
    <SitePage mainId="ct-main" {...props}>
      <NightHero
        id="ct-hero-title"
        icon={<MailOutlineRounded />}
        eyebrow={t('contact_us')}
        title={L('ct_hero_title', "We're here to help.")}
        sub={L('ct_hero_sub', 'Questions about a trip, your account or driving with Donkey Drive? Call us, write to us or come and see us.')}
        pb={{ xs: 16, md: 20 }}
      />

      {/* ── The ways to reach us, on cards over the hero's edge ── */}
      <Container component="section" aria-labelledby="ct-ways-title" maxWidth="lg" sx={{ position: 'relative', zIndex: 2, mt: { xs: -10, md: -13 } }}>
        <Typography component="h2" id="ct-ways-title" sx={srOnly}>
          {L('ct_ways_title', 'Ways to reach us')}
        </Typography>
        <Box
          component="ul"
          sx={{ listStyle: 'none', p: 0, m: 0, display: 'grid', gridTemplateColumns: { xs: '1fr', md: `repeat(${Math.max(ways.length, 1)}, 1fr)` }, gap: { xs: 2.5, md: 3 } }}
        >
          {ways.map((w, i) => (
            <Reveal component="li" key={w.id} delay={i * 100}>
              <Box
                component="a"
                href={w.href}
                {...(w.external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
                sx={{ ...cardLinkSx, display: 'flex', flexDirection: 'column', p: { xs: 3, md: 4 }, borderRadius: '24px', boxShadow: '0 40px 80px -48px rgba(19, 12, 9, 0.55)' }}
              >
                <Box aria-hidden sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: w.hue.bg, color: w.hue.fg, display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2.5 }}>
                  {w.icon}
                </Box>
                <Typography component="h3" sx={{ fontSize: 15, fontWeight: 600, color: c.INK_2, lineHeight: 1.4 }}>
                  {w.title}
                </Typography>
                <Typography dir="auto" sx={{ mt: 0.5, fontSize: { xs: 22, md: 24 }, fontWeight: 700, color: c.INK, lineHeight: 1.3, overflowWrap: 'anywhere' }}>
                  {w.value}
                </Typography>
                {w.note ? (
                  <Typography dir="auto" sx={{ mt: 0.5, fontSize: 15, color: c.INK_2, lineHeight: 1.5 }}>
                    {w.note}
                  </Typography>
                ) : null}
                <Box sx={{ mt: 'auto', pt: 2.5, display: 'inline-flex', alignItems: 'center', gap: 0.75, color: c.ACCENT_TEXT, fontWeight: 700, fontSize: 16 }}>
                  {w.action}
                  <ArrowForward aria-hidden sx={{ fontSize: 18 }} />
                  {w.external ? newTab : null}
                </Box>
              </Box>
            </Reveal>
          ))}
        </Box>
      </Container>

      {/* ── Follow us ── */}
      {socials.length ? (
        <Box component="section" aria-labelledby="ct-follow-title" sx={{ pt: { xs: 10, md: 14 }, pb: { xs: 2, md: 4 } }}>
          <Container maxWidth="lg">
            <Reveal>
              <Box sx={{ textAlign: 'center', maxWidth: 760, mx: 'auto' }}>
                <Typography sx={eyebrowSx(track)}>{L('ct_follow_eyebrow', 'Follow us')}</Typography>
                <Typography component="h2" id="ct-follow-title" sx={h2Sx(track)}>
                  {L('ct_follow_title', 'Follow Donkey Drive')}
                </Typography>
                <Typography sx={{ mt: 2, fontSize: { xs: 17, md: 19 }, lineHeight: 1.6, color: c.INK_2, textWrap: 'pretty' }}>
                  {L('ct_follow_sub', 'News and tips from Donkey Drive, where you already spend your time.')}
                </Typography>
              </Box>
            </Reveal>
            <Box
              component="ul"
              sx={{ listStyle: 'none', p: 0, m: 0, mt: { xs: 5, md: 7 }, display: 'grid', gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: `repeat(${Math.min(socials.length, 6)}, 1fr)` }, gap: { xs: 2, md: 2.5 } }}
            >
              {socials.map((s, i) => (
                <Reveal component="li" key={s.key} delay={i * 60}>
                  <Box
                    component="a"
                    href={s.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      ...cardLinkSx,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      gap: 1.25,
                      py: 3,
                      px: 2,
                      borderRadius: '20px',
                      '&:hover': { transform: 'translateY(-3px)', borderColor: c.ACCENT, color: c.ACCENT_TEXT },
                    }}
                  >
                    <Box aria-hidden sx={{ width: 52, height: 52, borderRadius: '50%', bgcolor: iconHues.neutral.bg, color: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: 26 } }}>
                      {s.icon}
                    </Box>
                    <Typography sx={{ fontSize: 16, fontWeight: 600, color: 'inherit' }}>{s.name}</Typography>
                    {newTab}
                  </Box>
                </Reveal>
              ))}
            </Box>
          </Container>
        </Box>
      ) : null}

      <AppBand
        id="ct-cta-title"
        title={L('ct_cta_title', 'Get the Donkey Drive app')}
        sub={L('ct_cta_sub', 'Book trips, follow your driver and pay your way, on iPhone and Android.')}
      />
    </SitePage>
  );
}
